import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyProfile, updateMyProfile, changeMyPassword } from '../../api/adminUsers';

/**
 * 헤더 우측 계정 메뉴
 *
 * 2026-09-23 사용자 요구로 신설. 이전에는 로그인 정보와 비밀번호 변경이
 * **사이드바 좌하단**에 있었다 — 관례와 반대 위치라 있어도 눈에 띄지 않았다.
 *
 * 한 패널 안에서 프로필 수정과 비밀번호 변경을 모두 끝낸다.
 * 모달을 또 띄우면 "메뉴 → 모달 → 폼" 3단이 되어 두 번 클릭할 일을 세 번 클릭하게 된다.
 */
const AccountMenu = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState('profile');   // 'profile' | 'password'
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({ name: '', email: '' });
    const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const panelRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const me = await getMyProfile();
            setProfile(me);
            setForm({ name: me.name || '', email: me.email || '' });
        } catch (e) {
            setError(e.message);
        }
    }, []);

    useEffect(() => {
        if (open) {
            setMessage('');
            setError('');
            load();
        }
    }, [open, load]);

    // 바깥 클릭·Esc 로 닫기
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const displayName = profile?.name || user?.username || '관리자';
    const initial = displayName.slice(0, 1).toUpperCase();

    const saveProfile = async () => {
        setBusy(true); setError(''); setMessage('');
        try {
            const saved = await updateMyProfile({ name: form.name, email: form.email });
            setProfile(saved);
            setMessage('저장했습니다.');
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };

    const savePassword = async () => {
        setError(''); setMessage('');
        if (pw.next !== pw.confirm) { setError('새 비밀번호가 서로 다릅니다.'); return; }
        setBusy(true);
        try {
            await changeMyPassword(pw.current, pw.next);
            setPw({ current: '', next: '', confirm: '' });
            setMessage('비밀번호를 변경했습니다.');
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };

    const doLogout = () => { logout(); navigate('/admin/login'); };

    const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
    const tabCls = (t) => `px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
        tab === t ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`;

    return (
        <div className="relative" ref={panelRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="true"
                className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            >
                <span className="w-7 h-7 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center">
                    {initial}
                </span>
                <span className="text-sm font-bold text-gray-800 hidden sm:inline">{displayName}</span>
                <span className={`text-gray-400 text-[10px] transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-gray-200 z-[1000] overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <p className="text-sm font-bold text-gray-900 m-0">{displayName}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {profile?.username}
                            {profile?.role === 'owner' && (
                                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-primary-50 text-primary-600 text-[10px] font-bold">소유자</span>
                            )}
                        </p>
                        {profile?.lastLoginAt && (
                            <p className="text-[11px] text-gray-400 mt-1">
                                마지막 로그인 {new Date(profile.lastLoginAt).toLocaleString('ko-KR')}
                            </p>
                        )}
                    </div>

                    <div className="px-4 pt-3 flex gap-2">
                        <button type="button" className={tabCls('profile')} onClick={() => { setTab('profile'); setError(''); setMessage(''); }}>내 정보</button>
                        <button type="button" className={tabCls('password')} onClick={() => { setTab('password'); setError(''); setMessage(''); }}>비밀번호 변경</button>
                    </div>

                    {(error || message) && (
                        <div className={`mx-4 mt-3 px-3 py-2 rounded-lg text-xs whitespace-pre-line ${
                            error ? 'bg-amber-50 border border-amber-200 text-amber-900' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                        }`}>
                            {error || message}
                        </div>
                    )}

                    <div className="p-4 space-y-3">
                        {tab === 'profile' ? (
                            <>
                                <div>
                                    <label htmlFor="acc-name" className="block text-xs font-bold text-gray-600 mb-1">이름</label>
                                    <input id="acc-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                </div>
                                <div>
                                    <label htmlFor="acc-email" className="block text-xs font-bold text-gray-600 mb-1">이메일</label>
                                    <input id="acc-email" type="email" autoComplete="email" className={inputCls} value={form.email} placeholder="선택" onChange={(e) => setForm({ ...form, email: e.target.value })} />
                                </div>
                                <p className="text-[11px] text-gray-400">아이디와 권한은 사용자 관리에서만 바꿀 수 있습니다.</p>
                                <button type="button" onClick={saveProfile} disabled={busy} className="w-full py-2 rounded-lg bg-primary-500 text-white text-sm font-bold disabled:opacity-50">
                                    {busy ? '저장 중…' : '저장'}
                                </button>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label htmlFor="acc-cur" className="block text-xs font-bold text-gray-600 mb-1">현재 비밀번호</label>
                                    <input id="acc-cur" type="password" autoComplete="current-password" className={inputCls} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
                                </div>
                                <div>
                                    <label htmlFor="acc-new" className="block text-xs font-bold text-gray-600 mb-1">새 비밀번호</label>
                                    <input id="acc-new" type="password" autoComplete="new-password" className={inputCls} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
                                </div>
                                <div>
                                    <label htmlFor="acc-confirm" className="block text-xs font-bold text-gray-600 mb-1">새 비밀번호 확인</label>
                                    <input id="acc-confirm" type="password" autoComplete="new-password" className={inputCls} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
                                </div>
                                <p className="text-[11px] text-gray-400">10자 이상, 영문·숫자·기호 중 두 종류 이상.</p>
                                <button type="button" onClick={savePassword} disabled={busy} className="w-full py-2 rounded-lg bg-primary-500 text-white text-sm font-bold disabled:opacity-50">
                                    {busy ? '변경 중…' : '비밀번호 변경'}
                                </button>
                            </>
                        )}
                    </div>

                    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                        <button type="button" onClick={doLogout} className="w-full py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-sm font-bold">
                            로그아웃
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountMenu;
